import { Button } from '@/common/ui/Form/Button'
import { Table, TableScrollHost, Tbody, Td, Th, Thead, Tr } from '@/common/ui/Table/Table'
import { Icon } from '@/common/ui/Icon'
import { Modal } from '@/common/ui/Modal'
import { cn } from '@/common/lib/cn/cn'
import { useOrgMembers, useRemoveMember } from '../models'
import type { PageMembershipResponse } from '@/common/api/client/types.gen'
import { useState } from 'react'
import { useToast } from '@/common/ui/Toast/toastContext'

const roleMap: Record<PageMembershipResponse['items'][number]['role'], string> = {
    owner: 'Владелец',
    admin: 'Администратор',
    manager: 'Менеджер',
    member: 'Участник',
    viewer: 'Просмотрщик',
}

export const OrgMembersTable = ({ className }: { className?: string }) => {
    const { orgMembers, currentOrg, isLoading, error } = useOrgMembers()
    const { mutate: removeMember } = useRemoveMember()
    const [memberToDelete, setMemberToDelete] = useState<string | null>(null)
    const toastContext = useToast()

    const handleDeleteClick = (userId: string) => {
        setMemberToDelete(userId)
        const checkbox = document.getElementById('confirm-delete-modal') as HTMLInputElement

        if (checkbox) {
            checkbox.checked = true
        }
    }

    const handleConfirmDelete = () => {
        if (memberToDelete) {
            removeMember(memberToDelete)
            setMemberToDelete(null)
            toastContext?.showToast({ type: 'success', message: 'Участник удален' })
            const checkbox = document.getElementById('confirm-delete-modal') as HTMLInputElement

            if (checkbox) {
                checkbox.checked = false
            }
        }
    }

    const handleCancelDelete = () => {
        setMemberToDelete(null)
        const checkbox = document.getElementById('confirm-delete-modal') as HTMLInputElement

        if (checkbox) {
            checkbox.checked = false
        }
    }

    if (isLoading) return <div>Loading...</div>
    if (error) return <div>Error: {error.message}</div>

    if (!currentOrg) {
        return
    }

    return (
        <>
            {orgMembers && orgMembers.items.length > 1 && (
                <>
                    <div className='grid-cols-2 mt-6'>
                        <h3 className='text-lg font-medium mb-2'>Управление командой</h3>
                    </div>
                    <TableScrollHost className={cn('grid-cols-2', className)} minHeight={false} outline={false}>
                        <Table border pinRows pinCols striped thBg>
                            <Thead>
                                <Tr>
                                    <Th>Имя Фамилия</Th>
                                    <Th>Роль</Th>
                                    <Th>Email</Th>
                                    <Th>Статус</Th>
                                    <Th className='text-center'>Действия</Th>
                                </Tr>
                            </Thead>

                            <Tbody>
                                {orgMembers.items.map((member, index) => (
                                    <Tr key={index}>
                                        <Td>
                                            {member.user?.first_name} {member.user?.last_name}
                                        </Td>
                                        <Td>{roleMap[member.role]}</Td>
                                        <Td>{member.user?.email}</Td>
                                        <Td>{member.user?.is_active ? 'Активен' : 'Неактивен'}</Td>
                                        <Td className='text-center'>
                                            {member.role !== 'owner' && (
                                                <Button
                                                    emphasis='neutral'
                                                    variant='ghost'
                                                    size='lg'
                                                    onClick={() => handleDeleteClick(member.user?.id ?? '')}
                                                >
                                                    <Icon name='trash' size='sm' />
                                                </Button>
                                            )}
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableScrollHost>
                </>
            )}
            <Modal id='confirm-delete-modal'>
                <h3>Подтверждение удаления</h3>
                <p>Вы уверены, что хотите удалить этого участника из организации?</p>
                <p>Это действие нельзя будет отменить.</p>

                <div className='modal-form-actions mt-5'>
                    <Button size='xl' emphasis='negative' variant='filled' onClick={handleConfirmDelete}>
                        Удалить
                    </Button>
                    <Button size='lg' emphasis='secondary' variant='ghost' onClick={handleCancelDelete}>
                        Отмена
                    </Button>
                </div>
            </Modal>
        </>
    )
}
